import React, {useState} from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import {MaterialIcons} from '@expo/vector-icons';
import I18n from 'i18n-js';
import styles, {chevronColor, chevronSize} from '../../../style/adminTools';
import {ListEmpty, ListUnavailable} from './ListState';
import RuleEngineDropdown from '../../rulesEngine/RuleEngineDropdown';
import useSettingSave from '../components/useSettingSave';
import {storeOnDemandModeKey} from './sectionConfig';

const onDemandOptions = () => [
  {label: I18n.t('customerSettings.storeOnDemandDisabled'), value: 'disabled'},
  {label: I18n.t('customerSettings.storeOnDemandEnabled'), value: 'enabled'},
  {label: I18n.t('customerSettings.storeOnDemandOnly'), value: 'only'}
];

const StoreSummary = ({store, chevron}) => (
  <>
    <View style={styles.storeNameWrap}>
      {chevron ? (
        <MaterialIcons name={chevron} size={chevronSize} color={chevronColor} />
      ) : null}
      <Text style={styles.settingLabel}>{store.name}</Text>
    </View>
    <Text style={styles.settingSavingText}>{store.store_type}</Text>
    {store.status === false ? (
      <View style={styles.groupBadge}>
        <Text
          style={styles.groupBadgeText}
          testID={`store-${store.id}-inactive`}
        >
          {I18n.t('customerSettings.storeInactive')}
        </Text>
      </View>
    ) : null}
  </>
);

// A compact version of SettingInputRow's select, indented under the store it belongs to.
const StoreOnDemandSetting = ({store, onSave, onSaveStateChange}) => {
  const settingKey = storeOnDemandModeKey(store.id);
  const id = `setting-${settingKey}`;
  const {draft, pending, error, save} = useSettingSave({
    settingKey,
    value: store.on_demand_mode,
    onSave,
    onSaveStateChange
  });
  return (
    <View testID={id}>
      <View style={styles.storeOnDemandPanel}>
        <Text style={styles.storeOnDemandLabel}>
          {I18n.t('customerSettings.storeOnDemandMode')}
        </Text>
        <View
          style={[
            styles.storeOnDemandControl,
            pending ? styles.settingInputLocked : null
          ]}
          pointerEvents={pending ? 'none' : 'auto'}
        >
          <RuleEngineDropdown
            options={onDemandOptions()}
            selectedValue={draft}
            onSelect={next => {
              if (!pending && next !== draft) save(next);
            }}
            testID={`${id}-dropdown`}
            error={!!error}
          />
        </View>
        {pending ? (
          <Text style={styles.settingSavingText}>
            {I18n.t('unsavedChanges.saving')}
          </Text>
        ) : null}
      </View>
      {error ? (
        <Text style={styles.settingErrorText} testID={`${id}-error`}>
          {error}
        </Text>
      ) : null}
    </View>
  );
};

// The backend sends on_demand_mode only for ShipStation stores, the one store type the mode applies to. Their rows
// open on a click to show that store's setting, so a tenant with several ShipStation stores sees one at a time.
const StoreRow = ({store, open, onToggle, onSave, onSaveStateChange}) => {
  const rowStyle = [styles.settingRow, styles.settingRowMain];
  const testID = `store-${store.id}`;
  if (!store.on_demand_mode || !onSave) {
    return (
      <View style={rowStyle} testID={testID}>
        <StoreSummary store={store} />
      </View>
    );
  }
  return (
    <>
      <TouchableOpacity
        style={rowStyle}
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{expanded: open}}
        testID={testID}
      >
        <StoreSummary
          store={store}
          chevron={open ? 'expand-more' : 'chevron-right'}
        />
      </TouchableOpacity>
      {open ? (
        <StoreOnDemandSetting
          store={store}
          onSave={onSave}
          onSaveStateChange={onSaveStateChange}
        />
      ) : null}
    </>
  );
};

const StoresList = ({stores, onSave, onSaveStateChange}) => {
  const [openStoreId, setOpenStoreId] = useState(null);
  if (!stores) return <ListUnavailable testID="stores-unavailable" />;
  if (stores.length === 0) {
    return (
      <ListEmpty
        testID="stores-empty"
        text={I18n.t('customerSettings.noStores', {
          defaultValue: 'No stores connected'
        })}
      />
    );
  }
  return (
    <View testID="stores-list">
      {stores.map(store => (
        <StoreRow
          key={store.id}
          store={store}
          open={openStoreId === store.id}
          onToggle={() =>
            setOpenStoreId(current => (current === store.id ? null : store.id))
          }
          onSave={onSave}
          onSaveStateChange={onSaveStateChange}
        />
      ))}
    </View>
  );
};

export default StoresList;
